import { useCallback, useEffect, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';
import { scan } from 'rxjs/operators';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

export * from './key-state-pairs';

const allCalculatedValues = new BehaviorSubject({});
export const valueMirror = new BehaviorSubject({}).pipe(
  scan((acc, { key, value, shouldDeleteKey, shouldReset }) => {
    if (shouldReset) {
      // only use shouldReset in tests
      return {};
    }

    if (shouldDeleteKey) {
      const accWithoutKey = { ...acc };
      delete accWithoutKey[key];
      return accWithoutKey;
    }
    return {
      ...acc,
      [key]: value,
    };
  }),
);
valueMirror.subscribe((all) => allCalculatedValues.next(all));

let map = {};
export const __testOnlyClearMap = () => (map = {});
let timeouts = {};
export const __testOnlyClearTimeouts = () => (timeouts = {});
export const __testOnlyGetTimeouts = () => timeouts;
const getFromMap = (key) => map[key];
const addToMap = (key, initialState) => (map[key] = new BehaviorSubject(initialState));

const useCreateSubscribable = (key, initialState) => {
  if (!getFromMap(key)) {
    addToMap(key, initialState);
  }
  return getFromMap(key);
};

const useSubject = (subject, key, initialState) => {
  const [state, setState] = useAsyncSafeState(initialState);
  const setter = useCallback((value) => subject.next(value), [subject]);

  useEffect(() => {
    // this is necessary so that a callback can still be passed to setState: setState(_current => _current + 1)
    // and still work with useReadSubscribableState by providing it with the final value after react
    // has called the setter function with the current value and calculated the new state
    valueMirror.next({ key, value: state });
  }, [key, state]);

  useEffect(() => {
    const sub = subject.subscribe(setState);
    return () => {
      sub.unsubscribe();
      timeouts[key] = setTimeout(() => {
        if (!subject.observers.length) {
          delete map[key];
          delete timeouts[key];
          valueMirror.next({ key, shouldDeleteKey: true });
        }
      }, 0);
    };
  }, [subject, key, setState]);

  return [state, setter];
};

export const useSubscribableState = ({ key, initialState }) => {
  const subject = useCreateSubscribable(key, initialState);
  return useSubject(subject, key, initialState);
};

export const getSubscribableStateValue = (key) => {
  return allCalculatedValues.value?.[key];
};

export const useReadSubscribableState = ({ key }) => {
  const [state, setState] = useAsyncSafeState();
  const ref = useRef();
  useEffect(() => {
    const sub = allCalculatedValues.subscribe((allValues) => {
      const wantedValue = allValues[key];
      if (wantedValue !== ref?.current) {
        ref.current = wantedValue;
        setState(wantedValue);
      }
    });
    return () => sub.unsubscribe();
  }, [setState, key]);

  return state;
};
